import { useEffect, useState } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import {
  buildWaveform,
  resampleWaveform,
  resonance,
  TransmissionSignal,
  WAVEFORM_DAYS,
  WAVEFORM_RESOLUTION,
} from '@/utils/readingWaveform';

export interface ResonanceMatch {
  userId: string;
  displayName: string | null;
  avatarUrl: string | null;
  score: number; // 0..1
  wave: number[];
}

export interface ReadingWaveformState {
  loading: boolean;
  wave: number[]; // resampled (length WAVEFORM_RESOLUTION)
  totalSignals: number;
  /** Most recent activity timestamp (ms) so cards can show "last tuned in". */
  lastSignalAt: number | null;
  /** Top resonance matches from people the user follows (sorted desc). */
  matches: ResonanceMatch[];
  /** Average resonance across the followed cohort, 0..1. */
  cohortResonance: number;
}

const EMPTY: ReadingWaveformState = {
  loading: false,
  wave: new Array(WAVEFORM_RESOLUTION).fill(0),
  totalSignals: 0,
  lastSignalAt: null,
  matches: [],
  cohortResonance: 0,
};

const sinceIso = () => {
  const d = new Date();
  d.setUTCDate(d.getUTCDate() - WAVEFORM_DAYS);
  return d.toISOString();
};

export function useReadingWaveform(): ReadingWaveformState {
  const { user, loading: authLoading } = useAuth();
  const [state, setState] = useState<ReadingWaveformState>(EMPTY);

  useEffect(() => {
    if (authLoading) return;
    if (!user) {
      setState(EMPTY);
      return;
    }

    let cancelled = false;
    setState((s) => ({ ...s, loading: true }));

    (async () => {
      try {
        const since = sinceIso();

        // 1. Pull this user's transmissions in window.
        const { data: mine, error: mineErr } = await supabase
          .from('transmissions')
          .select('created_at, status, rating')
          .eq('user_id', user.id)
          .gte('created_at', since)
          .limit(1000);

        if (mineErr) throw mineErr;

        const mineSignals = (mine ?? []) as TransmissionSignal[];
        const fullWave = buildWaveform(mineSignals);
        const wave = resampleWaveform(fullWave);
        const lastSignalAt = mineSignals.reduce<number | null>((acc, t) => {
          const ms = new Date(t.created_at).getTime();
          return acc === null || ms > acc ? ms : acc;
        }, null);

        // 2. Pull followed users' transmissions and compute resonance.
        const { data: follows } = await supabase
          .from('user_follows')
          .select('following_id')
          .eq('follower_id', user.id);

        const followingIds = (follows ?? []).map((f) => f.following_id).filter(Boolean);
        let matches: ResonanceMatch[] = [];
        let cohortResonance = 0;

        if (followingIds.length > 0) {
          const [{ data: theirTransmissions }, { data: profiles }] = await Promise.all([
            supabase
              .from('transmissions')
              .select('user_id, created_at, status, rating')
              .in('user_id', followingIds)
              .gte('created_at', since)
              .limit(5000),
            supabase.rpc('get_public_profiles', { _ids: followingIds }),
          ]);

          const byUser = new Map<string, TransmissionSignal[]>();
          for (const t of theirTransmissions ?? []) {
            const uid = (t as { user_id: string }).user_id;
            if (!byUser.has(uid)) byUser.set(uid, []);
            byUser.get(uid)!.push(t as TransmissionSignal);
          }

          const profileMap = new Map(
            (profiles ?? []).map((p) => [p.id as string, p as { id: string; display_name: string | null; avatar_url: string | null }])
          );

          matches = followingIds
            .map((uid) => {
              const theirWave = resampleWaveform(buildWaveform(byUser.get(uid) ?? []));
              const score = resonance(wave, theirWave);
              const profile = profileMap.get(uid);
              return {
                userId: uid,
                displayName: profile?.display_name ?? null,
                avatarUrl: profile?.avatar_url ?? null,
                score,
                wave: theirWave,
              };
            })
            .filter((m) => m.score > 0)
            .sort((a, b) => b.score - a.score)
            .slice(0, 5);

          if (matches.length > 0) {
            cohortResonance =
              matches.reduce((acc, m) => acc + m.score, 0) / matches.length;
          }
        }

        if (cancelled) return;
        setState({
          loading: false,
          wave,
          totalSignals: mineSignals.length,
          lastSignalAt,
          matches,
          cohortResonance,
        });
      } catch (err) {
        console.error('[useReadingWaveform] failed', err);
        if (!cancelled) setState({ ...EMPTY, loading: false });
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [user, authLoading]);

  return state;
}
